import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  Undo,
  Context,
  Heading,
  List,
  ContextWatchdog,
  Code,
  Strikethrough,
  Subscript,
  Superscript,
  WordCount,
  Underline
} from "ckeditor5";

import { CKEditor, CKEditorContext } from "@ckeditor/ckeditor5-react";
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "ckeditor5/ckeditor5.css";

const InputTextEditor = ({ field }) => {
  const {
    name,
    label,
    value = "",
    onChange,
    required,
    labelWidth,
    fieldWidth,
    characterLimit,
    defaultValue,
    viewOnly
  } = field;

  const editorRef = useRef(null);

  /* -------------------- Handle Change -------------------- */
  const handleEditorChange = (_, editor) => {
    if (viewOnly) return; // ❌ prevent change in readonly

    const html = editor.getData();

    // Get plain text length (not HTML)
    const text = editor.editing.view.document
      .getRoot()
      .getChild(0)?.data || "";

    if (characterLimit && text.length > characterLimit) return;

    onChange(html);
  };

  /* -------------------- Apply Default Value -------------------- */
  useEffect(() => {
    if (defaultValue && !value) {
      onChange(defaultValue);
    }
  }, [defaultValue]);

  /* -------------------- Sync External Value -------------------- */
  useEffect(() => {
    if (editorRef.current) {
      const currentData = editorRef.current.getData();
      if (currentData !== value) {
        editorRef.current.setData(value || "");
      }
    }
  }, [value]);

  return (
    <div className="form-group my-2">
      {label && (
        <label htmlFor={name} style={{ width: labelWidth || "25%" }}>
          {label}
          {required && <span className="required_mark"> *</span>}
        </label>
      )}

      <div
        className={`w-${fieldWidth ?? "75"} d-inline-block mx-2`}
        style={{
          border: "1px solid #dcdcdc",
          borderRadius: "4px"
        }}
      >
        <CKEditorContext context={Context} contextWatchdog={ContextWatchdog}>
          <CKEditor
            editor={ClassicEditor}
            data={value}
            onChange={handleEditorChange}
            onReady={(editor) => {
              editorRef.current = editor;

              /* ✅ Enable readonly BUT keep toolbar */
              if (viewOnly) {
                editor.enableReadOnlyMode("readonly-mode");
              }

              /* ✅ Fixed height + scrolling (INLINE ONLY) */
              const editableEl = editor.ui.getEditableElement();
              if (editableEl) {
                editableEl.style.height = "200px"; // 👈 FIXED HEIGHT
                editableEl.style.overflowY = "auto"; // 👈 SCROLL
              }
            }}
            config={{
              licenseKey: "GPL", 
              // licenseKey: "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NjgyNjIzOTksImp0aSI6ImUyYTI2YjFjLWU0MGUtNDlkNy1hNGRhLTU0OWEzYzI4NzNhYiIsInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiLCJzaCJdLCJ3aGl0ZUxhYmVsIjp0cnVlLCJsaWNlbnNlVHlwZSI6InRyaWFsIiwiZmVhdHVyZXMiOlsiKiJdLCJ2YyI6IjY3YWNlODZkIn0.iiTBDWF6Q8cbADcfG2L1TLteMzynLww3C28tT4FluGHt9Pfo9HcO0iSzLAergbPIQZVCkvkg3DYHw1iBbszeuA", 
              plugins: [
                Essentials,
                Paragraph,
                Heading,
                List,
                Bold,
                Italic,
                Undo,
                Code,
                Strikethrough,
                Subscript,
                Superscript,
                WordCount,
                Underline
              ],
              toolbar: {
                items: [
                  "undo",
                  "redo",
                  "|",
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "strikethrough",
                  "subscript",
                  "superscript",
                  "code",
                  "|",
                  "bulletedList",
                  "numberedList"
                ],
                shouldNotGroupWhenFull: false
              },
              isReadOnly: viewOnly // ✅ readonly + toolbar visible
            }}
          />
        </CKEditorContext>
      </div>
    </div>
  );
};

InputTextEditor.propTypes = {
  field: PropTypes.object.isRequired
};

export default InputTextEditor;
